import { t } from "@bezbolisno/i18n";
import { Brand } from "@bezbolisno/ui/brand";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { z } from "zod";
import { signIn } from "../lib/auth";
import { keys } from "../lib/keys";
import { sessionQuery } from "../lib/session";

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (session)
      throw redirect({
        to: search.redirect && search.redirect !== "/login" ? search.redirect : "/",
      });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect: back } = Route.useSearch();
  const { queryClient } = Route.useRouteContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const res = await signIn.email({ email, password });
    setPending(false);
    if (res.error) {
      // Better Auth's sign-in limiter answers 429 (production only): say so instead of
      // blaming the credentials, or the user retypes a correct password into a closed door.
      setError(res.error.status === 429 ? t.auth.tooManyAttempts : t.auth.invalid);
      return;
    }
    // The login route cached a null session in beforeLoad; replace it with the real one so
    // the authed layout's ensureQueryData sees it instead of bouncing back here.
    queryClient.removeQueries({ queryKey: keys.session });
    await queryClient.fetchQuery(sessionQuery);
    navigate({ to: back && back !== "/login" ? back : "/" });
  }

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <Brand />
        </div>
        <div className="card p-6">
          <form onSubmit={onSubmit} className="grid gap-3">
            <label className="block">
              <span className="lab">{t.auth.email}</span>
              <input
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="lukian@bezbolisno.local"
              />
            </label>
            <label className="block">
              <span className="lab">{t.auth.password}</span>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </label>
            {error ? <div className="callout callout--danger">{error}</div> : null}
            <button className="btn btn--primary mt-1" type="submit" disabled={pending}>
              {pending ? t.auth.signingIn : t.auth.signIn}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
